/**
 * EditOrganizationDrawer：支持单位管理抽屉
 * props: { open: bool, onClose: function, value: array, onChange: function, eventId: string|number }
 * 支持新增、删除、修改、拖动排序，保存时更新父组件 formData 并提交后端接口
 */
import { useState, useCallback, useEffect } from 'react';
import { Drawer, message } from 'antd';
import { DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SaveButton from '../../components/SaveButton';
import request from '@/request';

/**
 * SortableUnitItem：可拖拽排序的支持单位行
 * props: { item, index, onUpdate, onRemove }
 * isMain 为 true 时单位名称不可编辑且不可删除
 */
function SortableUnitItem({ item, index, onUpdate, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`flex items-center gap-3 bg-white p-3 rounded-lg border mb-3 ${
        isDragging
          ? 'border-blue-400 shadow-lg'
          : 'border-slate-200 hover:border-blue-300 hover:shadow-sm'
      } transition-all duration-200`}
    >
      <button
        ref={setActivatorNodeRef}
        {...listeners}
        type="button"
        className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-grab active:cursor-grabbing"
      >
        <HolderOutlined />
      </button>
      <div className="flex-shrink-0 w-6 text-slate-500 text-sm font-medium text-center">
        {index + 1}
      </div>
      <input
        aria-label="单位名称"
        placeholder="单位名称，如：主办单位"
        value={item.name}
        disabled={item.isMain}
        onChange={(e) => onUpdate(index, 'name', e.target.value)}
        className="w-36 min-w-0 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
      />
      <input
        aria-label="文案内容"
        placeholder="文案内容"
        value={item.desc}
        onChange={(e) => onUpdate(index, 'desc', e.target.value)}
        className="flex-1 min-w-0 px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
      />
      {!item.isMain ? (
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="flex-shrink-0 w-8 h-8 rounded-md text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors duration-200 flex items-center justify-center cursor-pointer"
        >
          <DeleteOutlined />
        </button>
      ) : (
        <div className="w-8 flex-shrink-0" />
      )}
    </div>
  );
}

export default function EditOrganizationDrawer({ open, onClose, value = [], onChange, eventId }) {
  const [units, setUnits] = useState([]);

  /*
   * 每次抽屉打开时，将 value 克隆并注入临时 _id 用于 dnd-kit 排序
   */
  useEffect(() => {
    if (open) {
      setUnits(
        value.map((u, i) => ({
          ...u,
          _id: u._id || `unit-${i}-${Date.now() + i}`,
        }))
      );
    }
  }, [open]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  /*
   * 拖拽结束，按新位置重新排序 units
   */
  const handleDragEnd = useCallback(({ active, over }) => {
    if (over && active.id !== over.id) {
      setUnits((prev) => {
        const oldIndex = prev.findIndex((u) => u._id === active.id);
        const newIndex = prev.findIndex((u) => u._id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, []);

  /*
   * 更新指定行的字段
   */
  const handleUpdate = useCallback((index, field, val) => {
    setUnits((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: val };
      return next;
    });
  }, []);

  /*
   * 删除指定行（isMain 行由 UI 层禁用删除按钮，此处不做额外判断）
   */
  const handleRemove = useCallback((index) => {
    setUnits((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /*
   * 在列表末尾追加一条空行
   */
  const handleAdd = useCallback(() => {
    setUnits((prev) => [
      ...prev,
      { _id: `new-${Date.now()}`, name: '', desc: '', isMain: false },
    ]);
  }, []);

  /*
   * 保存：校验 → 提交后端（当 eventId 存在时）→ 回调父组件 → 关闭抽屉
   */
  const handleSave = useCallback(async () => {
    for (const u of units) {
      if (!u.name.trim()) {
        message.warning('请填写所有单位名称');
        return;
      }
    }
    /* 去除临时 _id 字段后再提交 */
    const data = units.map(({ _id, ...rest }) => rest);
    if (eventId) {
      await request.put(`/events/v2/${eventId}`, { assistUnit: data });
      message.success('保存成功');
    }
    if (onChange) onChange(data);
    if (onClose) onClose();
  }, [units, eventId, onChange, onClose]);

  return (
    <Drawer title="支持单位" width={600} open={open} onClose={onClose}>
      <div className="pb-16">
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={units.map((u) => u._id)}
            strategy={verticalListSortingStrategy}
          >
            {units.map((unit, index) => (
              <SortableUnitItem
                key={unit._id}
                item={unit}
                index={index}
                onUpdate={handleUpdate}
                onRemove={handleRemove}
              />
            ))}
          </SortableContext>
        </DndContext>
        {units.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-slate-600 font-medium mb-1">暂无支持单位</p>
            <p className="text-sm text-slate-400">点击下方按钮添加</p>
          </div>
        )}
        <button
          type="button"
          onClick={handleAdd}
          className="w-full py-3 mt-3 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 cursor-pointer font-medium flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          添加支持单位
        </button>
      </div>
      <div className="fixed bottom-0 right-0 flex justify-end items-center w-[600px] bg-white p-4 border-t border-slate-200">
        <SaveButton text="保存" onSave={handleSave} />
      </div>
    </Drawer>
  );
}
